/**
 * Liste Ninja-like · fiche client · formulaire · relevé.
 * Métier dans features/clients/.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { facturxFetch } from "../api/client";
import { useHubBack } from "../mobile/hubBack";
import { Button } from "../components/Button";
import { ConfirmModal } from "../components/ConfirmModal";
import { PageHeader } from "../components/PageHeader";
import { ClientForm } from "../features/clients/ClientForm";
import { ClientListBar } from "../features/clients/ClientListBar";
import { ClientShow } from "../features/clients/ClientShow";
import { ClientStatement } from "../features/clients/ClientStatement";
import { ClientTable } from "../features/clients/ClientTable";
import { clientListMatch, clientMatches, type ClientListFilter } from "../features/clients/format";
import { clientProfileGaps } from "../legal/clientKind";
import { todayIso } from "../features/dashboard/period";
import {
  clientToDraft,
  emptyDraft,
  type Client,
  type ClientDraft,
  type ClientListItem,
  type ClientStatement as Statement,
} from "../features/clients/types";

type ClientsPageProps = {
  companyId: number;
  seedId?: number | null;
  onSeedUsed?: () => void;
  onNewQuote?: (clientId: number) => void;
  onOpenQuote?: (id: number) => void;
  onNewInvoice?: (clientId: number) => void;
  onOpenInvoice?: (id: number) => void;
  onNewRecurring?: (clientId: number) => void;
  onOpenRecurring?: (id: number) => void;
};
type View = "list" | "show" | "form" | "statement";
type BackTo = "list" | "show";
type Stats = Pick<ClientListItem, "balance_cents" | "paid_to_date_cents">;

export function ClientsPage({ companyId, seedId, onSeedUsed, onNewQuote, onOpenQuote, onNewInvoice, onOpenInvoice, onNewRecurring, onOpenRecurring }: ClientsPageProps) {
  const [rows, setRows] = useState<ClientListItem[]>([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<ClientListFilter>("all");
  const [view, setView] = useState<View>(seedId ? "show" : "list");
  const [backTo, setBackTo] = useState<BackTo>("list");
  const [client, setClient] = useState<Client | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [draft, setDraft] = useState<ClientDraft | null>(null);
  const [statement, setStatement] = useState<Statement | null>(null);
  const [stmtFrom, setStmtFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [stmtTo, setStmtTo] = useState(() => todayIso());
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [menuId, setMenuId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const backDepth = (view === "list" ? 0 : view === "show" || backTo === "list" ? 1 : 2) + (deleteTarget ? 1 : 0);
  useHubBack(backDepth, () => {
    if (deleteTarget) {
      setDeleteTarget(null);
      return;
    }
    setError("");
    setDraft(null);
    setStatement(null);
    if ((view === "form" || view === "statement") && backTo === "show" && client) {
      setView("show");
      return;
    }
    setClient(null);
    setStats(null);
    setView("list");
  });

  const load = useCallback(async () => {
    const params = new URLSearchParams({ company_id: String(companyId), archived: "1" });
    return facturxFetch<{ clients: ClientListItem[] }>(`clients.php?${params}`);
  }, [companyId]);

  const visible = useMemo(
    () => rows.filter((c) => clientMatches(c, q) && clientListMatch(c, filter)),
    [rows, q, filter],
  );

  function statsFrom(list: ClientListItem[], id: number, fallback?: Stats | null): Stats | null {
    const row = list.find((r) => r.id === id);
    if (!row) return fallback ?? null;
    return { balance_cents: row.balance_cents, paid_to_date_cents: row.paid_to_date_cents };
  }

  async function refresh() {
    const data = await load();
    setRows(data.clients);
    if (client) setStats(statsFrom(data.clients, client.id, stats));
    return data.clients;
  }

  useEffect(() => {
    if (!seedId) setView("list");
    setBackTo("list");
    setClient(null);
    setStats(null);
    setDraft(null);
    setStatement(null);
    setQ("");
    setMenuId(null);
    setDeleteTarget(null);
  }, [companyId]);

  useEffect(() => {
    let stop = false;
    setError("");
    load()
      .then((data) => {
        if (!stop) setRows(data.clients);
      })
      .catch((e) => {
        if (!stop) setError(e instanceof Error ? e.message : "Erreur");
      });
    return () => {
      stop = true;
    };
  }, [load]);

  async function loadClient(id: number): Promise<Client> {
    const data = await facturxFetch<{ client: Client }>(`clients.php?id=${id}`);
    try {
      const extra = await facturxFetch<{ pay: Client["pay"] }>(`client-pay.php?id=${id}`);
      return { ...data.client, pay: extra.pay };
    } catch {
      return { ...data.client, pay: { method: "transfer", label: "Virement" } };
    }
  }

  async function openShow(id: number) {
    setBusy(true);
    setError("");
    setDraft(null);
    setStatement(null);
    try {
      const next = await loadClient(id);
      setClient(next);
      setStats(statsFrom(rows, id, { balance_cents: 0, paid_to_date_cents: 0 }));
      setView("show");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!seedId) return;
    void openShow(seedId);
    onSeedUsed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedId]);

  async function openEdit(id: number, from: BackTo) {
    setBusy(true);
    setError("");
    setStatement(null);
    try {
      const next = await loadClient(id);
      setDraft(clientToDraft(next));
      setBackTo(from);
      if (from === "show") setClient(next);
      setView("form");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function loadStatement(id: number, fromDay: string, toDay: string) {
    const params = new URLSearchParams({ id: String(id) });
    if (fromDay) params.set("from", fromDay);
    if (toDay) params.set("to", toDay);
    const data = await facturxFetch<{ statement: Statement }>(`statement.php?${params}`);
    setStatement(data.statement);
  }

  async function openStatement(id: number, from: BackTo) {
    setBusy(true);
    setError("");
    setDraft(null);
    const fromDay = `${new Date().getFullYear()}-01-01`;
    const toDay = todayIso();
    setStmtFrom(fromDay);
    setStmtTo(toDay);
    try {
      await loadStatement(id, fromDay, toDay);
      setBackTo(from);
      setView("statement");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function changeStatementPeriod(fromDay: string, toDay: string) {
    if (!statement) return;
    setStmtFrom(fromDay);
    setStmtTo(toDay);
    setBusy(true);
    setError("");
    try {
      await loadStatement(statement.client.id, fromDay, toDay);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  function goList() {
    setError("");
    setDraft(null);
    setStatement(null);
    setClient(null);
    setStats(null);
    setView("list");
  }

  function goBack(from: BackTo = backTo) {
    setError("");
    setDraft(null);
    setStatement(null);
    if (from === "show" && client) {
      setView("show");
      return;
    }
    goList();
  }

  async function save() {
    if (!draft) return;
    const gaps = clientProfileGaps(draft);
    if (gaps.length) {
      setError("Fiche client incomplète : " + gaps.join(" · ") + ".");
      return;
    }
    setBusy(true);
    setError("");
    const body = { ...draft, company_id: companyId };
    try {
      if (draft.id) {
        const data = await facturxFetch<{ client: Client }>(`clients.php?id=${draft.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        setDraft(null);
        await refresh();
        if (backTo === "show") {
          setClient(data.client);
          setView("show");
        } else {
          setView("list");
        }
      } else {
        const data = await facturxFetch<{ client: Client }>("clients.php", {
          method: "POST",
          body: JSON.stringify(body),
        });
        setDraft(null);
        await refresh();
        setClient(data.client);
        setStats({ balance_cents: 0, paid_to_date_cents: 0 });
        setView("show");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  function sireneSummary(s: {
    checked: number;
    active: number;
    establishment_closed: number;
    ceased: number;
    unknown: number;
    skipped: number;
    failed: number;
  }): string {
    const bits: string[] = [];
    if (s.active) bits.push(`${s.active} active${s.active > 1 ? "s" : ""}`);
    if (s.establishment_closed) bits.push(`${s.establishment_closed} établissement${s.establishment_closed > 1 ? "s" : ""} fermé${s.establishment_closed > 1 ? "s" : ""}`);
    if (s.ceased) bits.push(`${s.ceased} cessée${s.ceased > 1 ? "s" : ""}`);
    if (s.unknown) bits.push(`${s.unknown} statut inconnu`);
    let msg = s.checked > 0 ? `${s.checked} vérifié${s.checked > 1 ? "s" : ""}` : "Aucun SIRET vérifié";
    if (bits.length) msg += ` : ${bits.join(", ")}`;
    if (s.skipped) msg += `. ${s.skipped} sans SIRET`;
    if (s.failed) msg += `. ${s.failed} sans réponse de l’INSEE`;
    return msg + ".";
  }

  async function checkAllSirene() {
    setChecking(true);
    setError("");
    setNotice("");
    try {
      const params = new URLSearchParams({ company_id: String(companyId) });
      if (filter === "archived") params.set("archived", "1");
      const data = await facturxFetch<{
        checked: number;
        active: number;
        establishment_closed: number;
        ceased: number;
        unknown: number;
        skipped: number;
        failed: number;
      }>(`client-sirene.php?${params}`, { method: "POST" });
      setNotice(sireneSummary(data));
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setChecking(false);
    }
  }

  async function checkSirene(id: number) {
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ client: Client }>(`client-sirene.php?id=${id}`, { method: "POST" });
      setClient(data.client);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function archive(id: number, archived: boolean) {
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ client: Client }>(`clients.php?id=${id}`, {
        method: "PATCH",
        body: JSON.stringify({ archived: !archived }),
      });
      await refresh();
      if (view === "show" || (view === "form" && backTo === "show")) {
        setClient(data.client);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!deleteTarget) return;
    setBusy(true);
    setError("");
    try {
      await facturxFetch(`clients.php?id=${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      setDraft(null);
      setStatement(null);
      if (client?.id === deleteTarget.id) {
        setClient(null);
        setStats(null);
      }
      await refresh();
      setView("list");
    } catch (e) {
      setDeleteTarget(null);
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  const viewKey =
    view === "show" && client
      ? `show-${client.id}`
      : view === "form"
        ? `form-${draft?.id ?? "new"}`
        : view === "statement" && statement
          ? `statement-${statement.client.id}`
          : "list";

  return (
    <div className="w-full">
      {error && view !== "form" ? <p className="mb-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">{error}</p> : null}
      {notice && view === "list" ? <p className="mb-3 rounded-lg bg-teal-50 px-3 py-2 text-sm text-teal-900">{notice}</p> : null}
      <div className="fx-page" key={viewKey}>
      {view === "list" ? (
        <>
          <PageHeader
            title="Clients"
            action={
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => void checkAllSirene()} disabled={busy || checking}>
                  {checking ? "Vérification…" : "Vérifier à l’INSEE"}
                </Button>
                <Button
                  onClick={() => {
                    setError("");
                    setNotice("");
                    setStatement(null);
                    setBackTo("list");
                    setDraft(emptyDraft());
                    setView("form");
                  }}
                  disabled={busy || checking}
                >
                  Nouveau client
                </Button>
              </div>
            }
          />
          <ClientListBar q={q} onQ={setQ} filter={filter} onFilter={setFilter} count={visible.length} />
          <ClientTable
            rows={visible}
            emptyText={q.trim() || filter !== "all" ? "Aucun client ne correspond." : "Aucun client."}
            menuId={menuId}
            onMenu={setMenuId}
            onOpen={(id) => {
              if (!busy) void openShow(id);
            }}
            onEdit={(id) => void openEdit(id, "list")}
            onStatement={(id) => void openStatement(id, "list")}
            onArchive={(row) => void archive(row.id, Boolean(row.archived))}
            onDelete={(row) => setDeleteTarget({ id: row.id, name: row.name })}
          />
        </>
      ) : null}
      {view === "show" && client ? (
        <ClientShow
          client={client}
          stats={stats}
          busy={busy}
          onBack={goList}
          onEdit={() => void openEdit(client.id, "show")}
          onStatement={() => void openStatement(client.id, "show")}
          onArchive={() => void archive(client.id, Boolean(client.archived))}
          onDelete={() => setDeleteTarget({ id: client.id, name: client.name })}
          onCheckSirene={() => void checkSirene(client.id)}
          onNewQuote={onNewQuote ? () => onNewQuote(client.id) : undefined}
          onOpenQuote={onOpenQuote}
          onNewInvoice={onNewInvoice ? () => onNewInvoice(client.id) : undefined}
          onOpenInvoice={onOpenInvoice}
          onNewRecurring={onNewRecurring ? () => onNewRecurring(client.id) : undefined}
          onOpenRecurring={onOpenRecurring}
          onLedgerChange={() => {
            void refresh().catch(() => undefined);
          }}
        />
      ) : null}
      {view === "form" && draft ? (
        <ClientForm
          draft={draft}
          setDraft={setDraft}
          busy={busy}
          setBusy={setBusy}
          error={error}
          setError={setError}
          onSave={() => void save()}
          onCancel={() => goBack()}
          onDelete={draft.id ? () => setDeleteTarget({ id: draft.id as number, name: draft.name }) : undefined}
        />
      ) : null}
      {view === "statement" && statement ? (
        <ClientStatement
          data={statement}
          from={stmtFrom}
          to={stmtTo}
          onPeriod={(fromDay, toDay) => void changeStatementPeriod(fromDay, toDay)}
          onClose={() => goBack()}
          onError={setError}
        />
      ) : null}
      </div>
      <ConfirmModal
        open={deleteTarget !== null}
        title="Supprimer ce client ?"
        message={
          deleteTarget
            ? `« ${deleteTarget.name} » sera retiré. Impossible s’il figure déjà sur une facture.`
            : undefined
        }
        busy={busy}
        onCancel={() => {
          if (!busy) setDeleteTarget(null);
        }}
        onConfirm={() => void remove()}
      />
    </div>
  );
}
